import React, { useState, useRef, useMemo } from 'react';
import Immutable, { List, OrderedMap } from 'immutable';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import {
  Image,
  Container,
  WysiwygField,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  CoreLink,
  Paragraph,
} from '@uncinc/react-drupal-core';
import { useTranslation } from 'react-i18next';
import Truncate from 'react-truncate';
import {
  SCREEN_MOBILE,
  SCREEN_NORMAL,
  SCREEN_TABLET,
  OVERVIEW_REDIRECT,
  CONTENT_IMAGE_PLACEHOLDER,
  NODE_TYPE_ACTIVITY_TARGET_AUDIENCE,
} from '../../../../../config/constants';

import Address from '../../Address';
import Dates from '../../Dates';
import Form from '../../Form';

import './style.scss';


const NodeActivityDetailRegenboog = ({ entity }) => {
  const { t } = useTranslation();
  const activitySignUpRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  const title = entity.getIn(['title', 0, 'value'], '');
  const body = entity.getIn(['body', 0, 'processed'], '');
  const hero = entity.getIn(['field_image', 0, 'styles'], '');
  const targetAudience = entity.getIn(['field_target_audience', 0, 'value'], NODE_TYPE_ACTIVITY_TARGET_AUDIENCE.PARTICIPANT);

  const entityOverviewUrl = useMemo(() => (
    OVERVIEW_REDIRECT['activities'][targetAudience][currentLanguage]
  ), [currentLanguage, targetAudience]);

  // setup schema.org jobPosting
  // get protocol and host from window location
  const pathname = useSelector((state) => state.getIn(['router', 'location', 'pathname']));
  const protocol = window.location.protocol;
  const host = window.location.host;
  const url = `${protocol}//${host}${pathname}`;
  const activityPostingLinkedData = createActivityPostingLd(entity)
    .set('url', url);
  const schemaString = JSON.stringify(activityPostingLinkedData.toJS(), null, 2);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {schemaString}
        </script>
      </Helmet>
      <article className="NodeActivityDetail">

        {/* Start of content */}
        <Container>
          <div className="row">
            <div className="col-12 col-lg-7 offset-lg-5 NodeVacancyFull__heroimage">
              {hero ? (
                <Image
                  lazyload
                  includeWebP
                  alt={title}
                  src={{
                    [SCREEN_MOBILE]: hero.getIn(['crop_4_3']),
                    [SCREEN_TABLET]: hero.getIn(['crop_4_3']),
                    [SCREEN_NORMAL]: hero.getIn(['crop_4_3']),
                  }}
                />
              ) : <img src={CONTENT_IMAGE_PLACEHOLDER} alt="Content placeholder" />}
            </div>
            <div className="col-12 col-md-10 col-lg-6 NodeVacancyFull__titleboxWrapper">
              <div className="NodeVacancyFull__titlebox">
                <div>
                  <CoreLink className="link" to={entityOverviewUrl}>
                    <i className="icon icon-arrow-left icon-variant-1" /> {t('activity.back_to_overview')}
                  </CoreLink>
                </div>
                <div>
                  <h1 className="asH2">
                    <Truncate lines={2} ellipsis="...">
                      {title}
                    </Truncate>
                  </h1>
                </div>
              </div>

              <Address entity={entity} />

            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <div className="Node__body">
                <WysiwygField
                  property="schema:text"
                  html={body}
                />
              </div>
            </div>
          </div>

        </Container>

        {/* Start of paragraphs */}
        {entity.get('field_paragraphs', List()).size > 0 ? (
          <div className="node--page--paragraphs-wrapper">
            {entity.get('field_paragraphs', List()).map((paragraph) => {
              const id = paragraph.get('target_id');
              return (
                <Paragraph
                  key={`paragraph:${id}`}
                  id={id}
                  entity={paragraph.get('entity')}
                />
              );
            })}
          </div>
        ) : null}
        {/* End of paragraphs */}

        {/* Start of dates */}
        <Dates
          entity={entity}
          activitySignUpRef={activitySignUpRef}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
        {/* End of dates */}

        {/* Start application webform */}
        <div ref={activitySignUpRef}>
          <Form
            entity={entity}
            activitySignUpRef={activitySignUpRef}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
        </div>
        {/* End application webform */}
      </article>
    </>
  );
};

export default NodeActivityDetailRegenboog;

function createActivityPostingLd(entity) {
  const metatag = entity.getIn(['metatag', 'value'], Immutable.Map());
  const image = entity.getIn(['field_image', 0, 'styles', 'large'], null);
  const dateRanges = entity.getIn(['field_date_ranges', 0, 'dates'], List());
  const firstUpcomingDate = dateRanges.get(0);

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Event',
    'name': metatag.getIn(['schema_event_name', 'attributes', 'content']),
    'description': metatag.getIn(['schema_event_description', 'attributes', 'content']),
    'identifier': {
      '@type': 'PropertyValue',
      'name': 'De Regenboog',
      'value': metatag.getIn(['schema_event_identifier', 'attributes', 'content']),
    },
    'location': metatag.getIn(['schema_event_location_0', 'attributes', 'content']),
    'image': image,
  };

  if (firstUpcomingDate) {
    const date = new Date(firstUpcomingDate.getIn(['start', 'date'])).toISOString();
    schema.startDate = date;
    schema.endDate = date;
  }

  return OrderedMap(schema);
}
